import React, { useEffect, useMemo } from "react"
import { cva } from "class-variance-authority"
import Label from "./Label"
import cn from "../../../utils/classes"
import RowContainer from "./Layout/RowContainer"

export type CheckboxSizes = "small" | "sm" | "medium" | "md" | "large" | "lg"

const sizeStyles = {
  small: "h-4 w-4",
  medium: "h-6 w-6",
  large: "h-8 w-8",
}

export const CheckboxSizeStyles: Record<CheckboxSizes, string> = {
  small: sizeStyles.small,
  sm: sizeStyles.small,
  medium: sizeStyles.medium,
  md: sizeStyles.medium,
  large: sizeStyles.large,
  lg: sizeStyles.large,
}

export interface CheckboxV2Props {
  label: string
  showLabel?: boolean
  checkboxId?: string
  checked?: boolean
  error?: boolean
  errorText?: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  isRequired?: boolean
  disabled?: boolean
  disabledReason?: string
  size?: CheckboxSizes
  styleOverrides?: string
  labelStyleOverrides?: string
  containerStyleOverrides?: string
  readOnly?: boolean
  tabIndex?: number
  onFocus?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  forceFocus?: boolean
}

export const CheckboxV2: React.FC<CheckboxV2Props> = ({
  label,
  checkboxId,
  checked = false,
  onChange,
  isRequired,
  disabled = false,
  disabledReason,
  size = "small",
  styleOverrides,
  showLabel = true,
  error = false,
  errorText,
  labelStyleOverrides,
  containerStyleOverrides,
  readOnly = false,
  tabIndex,
  onFocus,
  onKeyDown,
  forceFocus = false,
}) => {
  const containerVariants = cva(["relative", "items-center", "space-x-2"], {
    variants: {
      disabled: {
        true: "cursor-not-allowed",
      },
    },
  })

  const inputVariants = cva(
    [
      "!bg-55",
      "border",
      "border-solid",
      "rounded",
      "focus:ring-2",
      "focus:!ring-primary-blue",
      "dark:ring-offset-gray-800",
      "dark:bg-gray-700",
      "dark:border-gray-600",
    ],
    {
      variants: {
        disabled: {
          true: "cursor-not-allowed !text-gray-300 focus:!ring-none",
          false: "cursor-pointer !text-primary-blue focus:!ring-primary-blue",
        },
        error: {
          true: "border border-solid !border-outline-red",
          false: "border border-solid border-gray-300",
        },
        size: CheckboxSizeStyles,
        readOnly: {
          true: "cursor-not-allowed",
          false: "cursor-pointer",
        },
      },
    },
  )

  const labelContainerVariants = cva(
    ["ml-3 ", "text-sm", "leading-6", "flex flex-col", "items-start"],
    {
      variants: {
        showLabel: {
          true: "",
          false: "sr-only",
        },
      },
    },
  )

  const labelVariants = cva(["text-sm", "font-normal"], {
    variants: {
      disabled: {
        true: "cursor-not-allowed text-gray-500 font-normal",
        false: "cursor-pointer",
      },
      readOnly: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
    },
  })

  const containerClassName = useMemo(() => {
    return cn(containerVariants({ disabled }), containerStyleOverrides)
  }, [disabled, containerVariants, containerStyleOverrides])

  const inputClassName = useMemo(() => {
    return cn(
      inputVariants({ disabled, size, error, readOnly }),
      styleOverrides,
    )
  }, [inputVariants, disabled, size, error, readOnly, styleOverrides])

  const labelContainerClassName = useMemo(() => {
    return cn(labelContainerVariants({ showLabel }))
  }, [showLabel, labelContainerVariants])

  const labelClassName = useMemo(() => {
    return cn(labelVariants({ disabled, readOnly }), labelStyleOverrides)
  }, [disabled, readOnly, labelVariants, labelStyleOverrides])

  const disabledReasonClassName = useMemo(() => {
    return cn(["text-xs", "text-gray-500", "font-small"])
  }, [])

  const id = useMemo(() => {
    return checkboxId || crypto.randomUUID()
  }, [checkboxId])

  const errorMessageId = `${label}-error-message`
    .toLowerCase()
    .replace(" ", "-")

  useEffect(() => {
    if (forceFocus) {
      const checkbox = document.getElementById(id)
      checkbox?.focus()
    }
  }, [forceFocus, id])
  return (
    <div className={`w-full flex flex-col justify-center gap-y-2`}>
      <RowContainer styleOverrides={containerClassName}>
        <input
          id={id}
          name={checkboxId}
          type="checkbox"
          checked={checked}
          onChange={readOnly ? (e) => e.preventDefault() : onChange}
          className={`minimum-touchtarget ${inputClassName}`}
          disabled={disabled}
          aria-required={isRequired}
          aria-invalid={error}
          aria-errormessage={errorMessageId}
          aria-readonly={readOnly}
          tabIndex={tabIndex}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
        />
        <div className={labelContainerClassName}>
          <Label
            text={label}
            htmlFor={id}
            isRequired={isRequired}
            styleOverrides={labelClassName}
          />
          {(disabled || readOnly) && disabledReason && (
            <Label
              text={disabledReason}
              htmlFor={id}
              styleOverrides={disabledReasonClassName}
            />
          )}
        </div>
      </RowContainer>

      {error && errorText && (
        <span id={errorMessageId} role="alert" className="error-text mt-0">
          {errorText}
        </span>
      )}
    </div>
  )
}

export default CheckboxV2
